/*
 * Farida Gupta - upcoming exhibitions location filter, "Notify me" trigger and applied-filter
 * pills (theme/sections/upcoming-exhibitions.liquid). Extracted from an inline <script> tag into
 * this asset so it's cached like the section's own upcoming-exhibitions.css instead of being
 * re-sent as part of the page's HTML on every load.
 *
 * Plain top-level script (not a custom element, unlike most other fg-*.js files) since its core
 * behaviour is already document-level delegated listeners rather than something tied to one
 * element instance - see the note on `filterCheckboxSelector` below for why.
 */
function initUpcomingExhibitionsFilter(root) {
  root.addEventListener('click', function (event) {
    var button = event.target.closest('.upcoming-exhibitions__notify-btn');
    if (!button) return;

    document.dispatchEvent(new CustomEvent('fg:notify-city', { detail: { city: button.dataset.notifyCity } }));
  });

  // Location filter: each checkbox's value is "Region::City" (see the accordion markup in the
  // section). No checkboxes checked means no filter is applied, so every card stays visible.
  // There are two copies of the checkbox list in the DOM (the desktop sidebar and the mobile
  // <x-drawer>) - whichever copy the visitor interacts with, checking a box there is what
  // counts, since the other copy is hidden at that viewport width. Checkbox lookups below go
  // through `document`, not `root`: DialogElement (the <x-drawer> base class) reparents its
  // content to <body> when opened, so the drawer's checkboxes stop being descendants of `root`
  // at that point and a root-scoped query/listener would miss them.
  var filterCheckboxSelector = 'input[name="upcoming_exhibitions_location"]';

  // Per-region "Select all" (only rendered when a region has 2+ cities). Deliberately a
  // different `name` from the city checkboxes so it never counts as a filter, never becomes a
  // pill and is never matched against a card - it only checks/unchecks its region's cities, and
  // its own checked state is derived from theirs in syncSelectAll below.
  var selectAllSelector = 'input[name="upcoming_exhibitions_region_all"]';
  if (!root.querySelector(filterCheckboxSelector)) return;

  var cards = Array.prototype.slice.call(root.querySelectorAll('.upcoming-exhibitions__event'));
  var filterEmpty = root.querySelector('.upcoming-exhibitions__filter-empty');
  var filterCounts = Array.prototype.slice.call(root.querySelectorAll('[data-upcoming-exhibitions-filter-count]'));
  var activeFilters = root.querySelector('[data-upcoming-exhibitions-active-filters]');
  var closeIconMarkup = (activeFilters && activeFilters.dataset.closeIcon) || '';

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  // Applied-filter pills below the mobile bar (same pattern as the collection page's
  // snippets/active-facets.liquid - .removable-facet/.text-subdued/.facets-clear-all are its
  // classes, reused directly). Built from the checked inputs directly rather than from Liquid,
  // since Liquid has no way to know which values are checked at render time.
  function renderActiveFilters(checkedInputs) {
    if (!activeFilters) return;

    if (checkedInputs.length === 0) {
      activeFilters.innerHTML = '';
      activeFilters.hidden = true;
      return;
    }

    var seenValues = {};
    var pillsHtml = '';

    checkedInputs.forEach(function (input) {
      if (seenValues[input.value]) return;
      seenValues[input.value] = true;

      var labelEl = input.closest('.checkbox-control');
      labelEl = labelEl ? labelEl.querySelector('label') : null;
      var labelText = labelEl ? labelEl.textContent : input.value;

      pillsHtml += '<div class="removable-facet" data-upcoming-exhibitions-active-filter="' + escapeHtml(input.value) + '">' +
        '<span class="text-subdued">' + escapeHtml(labelText) + '</span>' +
        '<button type="button" class="tap-area link-faded" data-upcoming-exhibitions-remove-filter="' + escapeHtml(input.value) + '" aria-label="Remove ' + escapeHtml(labelText) + '">' +
        closeIconMarkup +
        '</button>' +
      '</div>';
    });

    if (Object.keys(seenValues).length > 1) {
      pillsHtml += '<button type="button" class="facets-clear-all text-subdued" data-upcoming-exhibitions-filter-clear-inline>' +
        '<span class="link">Clear all</span>' +
      '</button>';
    }

    activeFilters.innerHTML = pillsHtml;
    activeFilters.hidden = false;
  }

  function recomputeFilters() {
    var checkedInputs = Array.prototype.slice.call(document.querySelectorAll(filterCheckboxSelector + ':checked'));
    // De-duplicated by value: the desktop sidebar and mobile drawer each hold a copy of every
    // checkbox, and "Select all" ticks both copies, so a raw checked-input count would double.
    var checked = [];
    checkedInputs.forEach(function (input) {
      if (checked.indexOf(input.value) === -1) checked.push(input.value);
    });

    var visibleCount = 0;

    cards.forEach(function (card) {
      var key = card.dataset.region + '::' + card.dataset.city;
      var visible = checked.length === 0 || checked.indexOf(key) !== -1;
      card.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    if (filterEmpty) {
      filterEmpty.hidden = visibleCount !== 0;
    }

    filterCounts.forEach(function (el) {
      el.textContent = checked.length;
    });

    renderActiveFilters(checkedInputs);
    syncSelectAll();
  }

  function cityInputsForRegion(region) {
    var prefix = region + '::';
    return Array.prototype.slice.call(document.querySelectorAll(filterCheckboxSelector)).filter(function (input) {
      return input.value.indexOf(prefix) === 0;
    });
  }

  // Checked only once every city in the region is checked, so it also unticks itself when a
  // single city is removed via its pill, "Clear all", or unticking it directly.
  function syncSelectAll() {
    Array.prototype.slice.call(document.querySelectorAll(selectAllSelector)).forEach(function (input) {
      var cityInputs = cityInputsForRegion(input.value);
      input.checked = cityInputs.length > 0 && cityInputs.every(function (cityInput) {
        return cityInput.checked;
      });
    });
  }

  function uncheckByValue(value) {
    Array.prototype.slice.call(document.querySelectorAll(filterCheckboxSelector)).forEach(function (input) {
      if (input.value === value) input.checked = false;
    });
  }

  function clearAllFilters() {
    Array.prototype.slice.call(document.querySelectorAll(filterCheckboxSelector + ':checked')).forEach(
      function (input) {
        input.checked = false;
      }
    );
    recomputeFilters();
  }

  document.addEventListener('change', function (event) {
    if (event.target.matches(selectAllSelector)) {
      var shouldCheck = event.target.checked;
      cityInputsForRegion(event.target.value).forEach(function (input) {
        input.checked = shouldCheck;
      });
      recomputeFilters();
      return;
    }

    if (!event.target.matches(filterCheckboxSelector)) return;

    // Mirror onto the other copy of this same city, so both copies always agree - otherwise
    // unticking a city that "Select all" had ticked in both copies leaves the hidden one checked.
    var changedInput = event.target;
    Array.prototype.slice.call(document.querySelectorAll(filterCheckboxSelector)).forEach(function (input) {
      if (input.value === changedInput.value) input.checked = changedInput.checked;
    });
    recomputeFilters();
  });

  var clearButton = root.querySelector('[data-upcoming-exhibitions-filter-clear]');
  if (clearButton) {
    clearButton.addEventListener('click', clearAllFilters);
  }

  if (activeFilters) {
    activeFilters.addEventListener('click', function (event) {
      var removeTrigger = event.target.closest('[data-upcoming-exhibitions-remove-filter]');
      if (removeTrigger) {
        uncheckByValue(removeTrigger.getAttribute('data-upcoming-exhibitions-remove-filter'));
        recomputeFilters();
        return;
      }

      if (event.target.closest('[data-upcoming-exhibitions-filter-clear-inline]')) {
        clearAllFilters();
      }
    });
  }
}

document.querySelectorAll('.upcoming-exhibitions').forEach(initUpcomingExhibitionsFilter);
